/**
 * Interactive size guide — illustrated living room, advisor notes, drag placement.
 */

function getSizeFromButton(btn) {
  return {
    key: btn.dataset.sizeKey || "",
    widthCm: Number(btn.dataset.widthCm),
    heightCm: Number(btn.dataset.heightCm),
    cmLabel: btn.dataset.cmLabel || "",
    inLabel: btn.dataset.inLabel || "",
  };
}

class KkhSizeGuide {
  #root;
  #room;
  #wallZone;
  #frame;
  #dims;
  #advisor;
  #advisorText;
  #buttons;
  #defaultKey;
  #variantRoot;
  #advisorMap = {};
  #syncing = false;
  #wallCm;
  #posX = 50;
  #posY = 42;
  #dragging = false;
  #dragOffset = { x: 0, y: 0 };
  #frameSize = { w: 0, h: 0 };

  constructor(root) {
    this.#root = root;
    this.#room = root.querySelector("[data-room]");
    this.#wallZone = root.querySelector("[data-wall-zone]");
    this.#frame = root.querySelector("[data-frame]");
    this.#dims = root.querySelector("[data-dims]");
    this.#advisor = root.querySelector("[data-advisor]");
    this.#advisorText = root.querySelector("[data-advisor-text]");
    this.#buttons = [...root.querySelectorAll("[data-size-btn]")];
    this.#defaultKey = root.dataset.defaultSizeKey || "30 × 45 cm (12 × 18 in)";
    this.#wallCm = Number(root.dataset.wallCm) || 360;
    this.#variantRoot = document.querySelector("variant-radios");
    this.#loadAdvisorMap();
    this.#init();
  }

  #loadAdvisorMap() {
    const el = this.#root.querySelector("[data-advisor-map]");
    if (!el) return;
    try {
      this.#advisorMap = JSON.parse(el.textContent);
    } catch {
      this.#advisorMap = {};
    }
  }

  #init() {
    if (!this.#frame || this.#buttons.length === 0) return;

    this.#buttons.forEach((btn) => {
      btn.addEventListener("click", () => this.#selectButton(btn, true));
    });

    this.#setupDrag();

    this.#variantRoot?.addEventListener("change", () => this.#syncFromVariant());
    document.addEventListener("variant:changed", () => this.#syncFromVariant());

    const initial = this.#getVariantSize() || this.#defaultKey;
    this.#selectByKey(initial, false);

    let resizeTimer;
    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => this.#render(), 120);
    });
  }

  #setupDrag() {
    if (!this.#frame || !this.#wallZone) return;

    this.#frame.addEventListener("pointerdown", (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      this.#dragging = true;
      this.#frame.setPointerCapture(e.pointerId);
      this.#frame.classList.add("is-dragging");

      const frameRect = this.#frame.getBoundingClientRect();
      this.#dragOffset = {
        x: e.clientX - frameRect.left - frameRect.width / 2,
        y: e.clientY - frameRect.top - frameRect.height / 2,
      };
    });

    this.#frame.addEventListener("pointermove", (e) => {
      if (!this.#dragging) return;
      this.#moveFrameToPointer(e.clientX, e.clientY);
    });

    const endDrag = (e) => {
      if (!this.#dragging) return;
      this.#dragging = false;
      this.#frame.classList.remove("is-dragging");
      if (this.#frame.hasPointerCapture(e.pointerId)) {
        this.#frame.releasePointerCapture(e.pointerId);
      }
    };

    this.#frame.addEventListener("pointerup", endDrag);
    this.#frame.addEventListener("pointercancel", endDrag);
  }

  #moveFrameToPointer(clientX, clientY) {
    const zoneRect = this.#wallZone.getBoundingClientRect();
    if (!zoneRect.width || !this.#frameSize.w) return;

    const halfW = this.#frameSize.w / 2;
    const halfH = this.#frameSize.h / 2;

    let centerX = clientX - zoneRect.left - this.#dragOffset.x;
    let centerY = clientY - zoneRect.top - this.#dragOffset.y;

    centerX = Math.max(halfW, Math.min(zoneRect.width - halfW, centerX));
    centerY = Math.max(halfH, Math.min(zoneRect.height - halfH, centerY));

    this.#posX = (centerX / zoneRect.width) * 100;
    this.#posY = (centerY / zoneRect.height) * 100;
    this.#applyFramePosition();
  }

  #applyFramePosition() {
    this.#frame.style.left = `${this.#posX}%`;
    this.#frame.style.top = `${this.#posY}%`;
    this.#frame.style.transform = "translate(-50%, -50%)";
  }

  #clampPosition() {
    const zoneRect = this.#wallZone?.getBoundingClientRect();
    if (!zoneRect?.width || !this.#frameSize.w) return;

    const halfW = (this.#frameSize.w / zoneRect.width) * 50;
    const halfH = (this.#frameSize.h / zoneRect.height) * 50;

    this.#posX = Math.max(halfW, Math.min(100 - halfW, this.#posX));
    this.#posY = Math.max(halfH, Math.min(100 - halfH, this.#posY));
    this.#applyFramePosition();
  }

  #getVariantSize() {
    const checked = this.#variantRoot?.querySelector('input[name="Size"]:checked');
    return checked?.value || "";
  }

  #syncFromVariant() {
    if (this.#syncing) return;
    const size = this.#getVariantSize();
    if (size) this.#selectByKey(size, false);
  }

  #selectByKey(key, updateVariant) {
    const btn =
      this.#buttons.find((b) => b.dataset.sizeKey === key) ||
      this.#buttons.find((b) => key.includes(b.dataset.cmLabel)) ||
      this.#buttons[1] ||
      this.#buttons[0];
    if (btn) this.#selectButton(btn, updateVariant);
  }

  #selectButton(btn, updateVariant) {
    const size = getSizeFromButton(btn);
    if (!size.widthCm || !size.heightCm) return;

    this.#buttons.forEach((b) => {
      const on = b === btn;
      b.classList.toggle("is-active", on);
      b.setAttribute("aria-pressed", on ? "true" : "false");
    });

    if (updateVariant) this.#setVariantSize(size.key);
    this.#updateAdvisor(size.key);
    this.#render(size);
  }

  #updateAdvisor(key) {
    if (!this.#advisor || !this.#advisorText) return;
    const note = this.#advisorMap[key] || "";
    this.#advisorText.textContent = note;
    this.#advisor.classList.toggle("is-empty", !note);
  }

  #setVariantSize(key) {
    if (!this.#variantRoot || !key) return;
    const input = this.#variantRoot.querySelector(`input[name="Size"][value="${CSS.escape(key)}"]`);
    if (!input || input.checked) return;
    this.#syncing = true;
    input.checked = true;
    input.dispatchEvent(new Event("change", { bubbles: true }));
    window.requestAnimationFrame(() => {
      this.#syncing = false;
    });
  }

  #render(size = null) {
    if (!size) {
      const active = this.#buttons.find((b) => b.classList.contains("is-active"));
      if (!active) return;
      size = getSizeFromButton(active);
    }

    const wallRect = this.#wallZone?.getBoundingClientRect();
    if (!wallRect?.width) return;

    const pxPerCm = wallRect.width / this.#wallCm;
    const pad = 14;
    const printW = size.widthCm * pxPerCm;
    const printH = size.heightCm * pxPerCm;
    const totalW = printW + pad;
    const totalH = printH + pad;

    const maxW = wallRect.width * 0.92;
    const maxH = wallRect.height * 0.92;
    const scale = Math.min(1, maxW / totalW, maxH / totalH);

    this.#frameSize = { w: totalW * scale, h: totalH * scale };

    this.#frame.hidden = false;
    this.#frame.style.width = `${this.#frameSize.w}px`;
    this.#frame.style.height = `${this.#frameSize.h}px`;

    this.#clampPosition();

    if (this.#dims) {
      this.#dims.textContent = `${size.cmLabel} · ${size.inLabel}`;
    }
  }
}

function init() {
  document.querySelectorAll("[data-kkh-size-guide]").forEach((section) => {
    new KkhSizeGuide(section);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
